import FormattedMessage from 'I18n/components/FormattedMessage';
import { memo } from 'react';
import { Button } from 'foundations-components/transitional/components/Button';
import { List } from 'foundations-components/transitional/components/List';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import useHasAssociatedObjects from '../hooks/useHasAssociatedObjects';
import useCanConvertQuoteToInvoice from '../hooks/useCanConvertQuoteToInvoice';
import { getQuoteId, getQuoteProperty } from 'quotes-ui-lib/utils/quote';
import { getIsPaidOrProcessing, getIsPublished, getIsArchived } from 'quotes-ui-lib/utils/status';
import { jsx as _jsx } from "react/jsx-runtime";
const getTooltipHeader = (error, quoteInPaidOrProcessingStatus, disabledReasons, unknownValidationError) => !error && !quoteInPaidOrProcessingStatus && disabledReasons.length > 0 && !unknownValidationError ? /*#__PURE__*/_jsx(FormattedMessage, {
  message: "quotesUiLib.convertToInvoiceButton.tooltip.header"
}) : null;
const getTooltipTitle = (error, quoteInPaidOrProcessingStatus, disabledReasons, unknownValidationError) => {
  switch (true) {
    case error:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "quotesUiLib.convertToInvoiceButton.error"
      });
    case unknownValidationError:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "quotesUiLib.convertToInvoiceButton.errorValidationInvoices"
      });
    case quoteInPaidOrProcessingStatus:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "quotesUiLib.convertToInvoiceButton.tooltip.paidOrProcessing"
      });
    default:
      return /*#__PURE__*/_jsx(List, {
        styled: true,
        wrapChildren: true,
        children: disabledReasons
      });
  }
};
const ConvertToInvoiceButton = ({
  quote,
  onClick,
  showModal,
  portalId
}) => {
  const quoteId = String(getQuoteId(quote));
  const quoteCurrency = getQuoteProperty(quote, 'hs_currency');
  const quoteInPaidOrProcessingStatus = getIsPaidOrProcessing(quote);
  const {
    hasInvoices,
    hasPaymentSchedules,
    hasMoreThanOneDiscount,
    error: errorFetchAssociatedObjects,
    loading: loadingFetchAssociatedObjects
  } = useHasAssociatedObjects({
    quoteId,
    portalId,
    skip: quoteInPaidOrProcessingStatus
  });
  const {
    validationErrorMessages,
    loading: loadingValidateInvoice,
    unknownValidationError
  } = useCanConvertQuoteToInvoice({
    quoteId,
    quoteCurrency,
    portalId,
    skip: quoteInPaidOrProcessingStatus
  });
  const disabledReasons = [...validationErrorMessages, hasPaymentSchedules && /*#__PURE__*/_jsx(FormattedMessage, {
    message: "quotesUiLib.convertToInvoiceButton.tooltip.paymentSchedule"
  }, "quotesUiLib.convertToInvoiceButton.tooltip.paymentSchedule"), hasMoreThanOneDiscount && /*#__PURE__*/_jsx(FormattedMessage, {
    message: "quotesUiLib.convertToInvoiceButton.tooltip.discounts"
  }, "quotesUiLib.convertToInvoiceButton.tooltip.discounts")].filter(Boolean);
  const isButtonDisabled = errorFetchAssociatedObjects || quoteInPaidOrProcessingStatus || unknownValidationError || disabledReasons.length > 0;
  const isButtonLoading = loadingFetchAssociatedObjects || loadingValidateInvoice;
  const handleOnClick = () => {
    if (hasInvoices) {
      showModal();
    } else {
      onClick();
    }
  };
  return /*#__PURE__*/_jsx(Tooltip, {
    "data-test-id": "invoice-btn-tooltip",
    headingText: getTooltipHeader(errorFetchAssociatedObjects, quoteInPaidOrProcessingStatus, disabledReasons, unknownValidationError),
    disabled: !isButtonDisabled || isButtonLoading,
    placement: "top left",
    use: errorFetchAssociatedObjects || quoteInPaidOrProcessingStatus || unknownValidationError ? 'default' : 'longform',
    title: getTooltipTitle(errorFetchAssociatedObjects, quoteInPaidOrProcessingStatus, disabledReasons, unknownValidationError),
    children: /*#__PURE__*/_jsx(Button, {
      "data-test-id": "invoice-btn",
      "data-selenium-test": "invoice-btn",
      use: "tertiary-light",
      responsive: false,
      size: "extra-small",
      onClick: handleOnClick,
      disabled: isButtonDisabled || isButtonLoading,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "quotesUiLib.convertToInvoiceButton.text"
      })
    }, "invoice-button")
  }, "convertToInvoiceButtonTooltip");
};
export default /*#__PURE__*/memo(ConvertToInvoiceButton);
export const showConvertToInvoiceButton = (quote, isScoped) => {
  return isScoped && getIsPublished(quote) && !getIsArchived(quote);
};